import { useEffect, useRef } from 'react';
import { supabase } from '../supabase/client';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';

const INACTIVITY_TIMEOUT = 12 * 60 * 1000; // 12 minutos en milisegundos
const WARNING_TIMEOUT = 9 * 60 * 1000; // 9 minutos en milisegundos (3 minutos antes de cierre)

export const useAutoLogout = () => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const warningTimeoutRef = useRef<NodeJS.Timeout>();
  const toastIdRef = useRef<string | number>();
  const { t } = useLanguage();
  
  console.log('[useAutoLogout] Hook function called');

  const resetTimer = () => {
    console.log('[useAutoLogout] resetTimer called');
    // Eliminar timeouts existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Eliminar toast de advertencia si existe
    if (toastIdRef.current) {
      toast.dismiss(toastIdRef.current);
    }
    
    // Configurar timeout de advertencia (7 minutos)
    warningTimeoutRef.current = setTimeout(() => {
      console.log('[useAutoLogout] Warning timeout triggered - showing inactivity warning');
      // Mostrar toast de advertencia con estilo mejorado
      toastIdRef.current = toast(
        t('sessionTimeout.warningMessage'),
        {
          position: "top-center",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          closeButton: true,
          className: "bg-white border border-amber-200 shadow-lg",
          icon: '⏱️',
          toastId: 'inactivity-warning'
        }
      );
    }, WARNING_TIMEOUT);
    
    // Configurar timeout de cierre de sesión (10 minutos)
    timeoutRef.current = setTimeout(async () => {
      console.log('[useAutoLogout] Main timeout triggered - attempting to sign out user');
      // Eliminar toast de advertencia si existe
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      
      // Mostrar toast de cierre de sesión con estilo mejorado
      console.log('[useAutoLogout] Showing session expired toast');
      toast(
        t('sessionTimeout.closedMessage'),
        {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          className: "bg-white border border-blue-100 shadow-lg",
          icon: '🔒',
          toastId: 'session-expired'
        }
      );
      
      // Realizar el cierre de sesión
      try {
        console.log('[useAutoLogout] About to call supabase.auth.signOut()');
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        console.log('[useAutoLogout] supabase.auth.signOut() completed successfully');
      } catch (error) {
        // Handle AuthSessionMissingError gracefully - this means the session was already invalid
        if (error instanceof Error && (
          error.message.includes('Auth session missing') || 
          error.message.includes('session_not_found') ||
          error.name === 'AuthSessionMissingError'
        )) {
          console.warn('[useAutoLogout] Session was already invalid, auto logout completed locally:', error.message);
          // Session is already invalid on server, but local logout will still work
          return;
        }
        
        // Log other errors as actual errors and show toast
        console.error('[useAutoLogout] Unexpected error during auto logout:', error);
        toast.error(t('common.error'), {
          position: "top-center",
          autoClose: 3000,
          className: "bg-white border border-red-100 shadow-lg"
        });
      }
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    console.log('[useAutoLogout] useEffect hook triggered');
    // Eventos para detectar actividad del usuario
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Resetear timer con cualquier actividad
    const handleUserActivity = () => {
      resetTimer();
    };

    // Agregar event listeners
    console.log('[useAutoLogout] Adding event listeners');
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Configuración inicial del timer
    console.log('[useAutoLogout] Setting up initial timer');
    resetTimer();

    // Limpieza
    return () => {
      console.log('[useAutoLogout] useEffect cleanup function called');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [t]);

  return null;
};